import { useState, useEffect, useContext } from "react";
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableHead,
    TableRow,
    TablePagination,
    Toolbar,
    IconButton,
    Box,
    Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getTrackEvent, downloadEventList } from "api/layer";
import { AppContext, AppAction } from "helpers/context";
import styles from "./burialPointDetails.module.scss";
import SearchInput from "./search-input/search-input";
import StartTimeDropdown from "./start-time-dropdown/start-time-dropdown";
import EndTimeDropdown from "./end-time-dropdown/end-time-dropdown";


function BurialPointDetails() {
    const [context, dispatch] = useContext(AppContext);
    const [t] = useTranslation();
    const [showFilters, setShowFilters] = useState(false);
    const [searchData, setSearchData] = useState<{
        keyWord: string;
        startOccurTime: any;
        endOccurTime: any;
    }>({
        keyWord: "",
        startOccurTime: null,
        endOccurTime: null,
    });
    const [reverseSelect, setReverseSelect] = useState(false);
    const [page, setPage] = useState(0);
    const [tableData, setTableData] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [sortByField, setSortByField] = useState("occur_time");
    const [sortOrder, setSortOrder] = useState<{
        [key: string]: "asc" | "desc",
    }>({
        client_name: "asc",
        bill_name: "asc",
        key_code: "asc",
        req_params: "asc",
        req_body: "asc",
        layers: "asc",
        http_method: "asc",
        location: "asc",
        location_length: "asc",
        endpoint_name: "asc",
        occur_time: "desc",
        status: "asc",
        error_info: "asc",
    });

    const toggleFilters = () => {
        setShowFilters((prev) => !prev);
    };

    const hanldeDownload = () => {
        const filteredData = Object.fromEntries(
            Object.entries(searchData).filter(([key, value]) => value !== null && value !== '')
        );
        if (filteredData.startOccurTime) {
            filteredData.startOccurTime = filteredData.startOccurTime.format("YYYY-MM-DD");
        }
        if (filteredData.endOccurTime) {
            filteredData.endOccurTime = filteredData.endOccurTime.format("YYYY-MM-DD");
        }
        downloadEventList({ ...filteredData, language: context.language }).then((res: any) => {
            const url = window.URL.createObjectURL(new Blob([res], { type: "application/force-download;charset=utf-8" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${t("userSystem.burialPointDetails")}.xls`);
            document.body.appendChild(link);
            link.click();
            link.onload = () => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            };
        });
    };

    const handleSearch = (params: any, is: boolean) => {
        setSearchData((prevData) => ({ ...prevData, [params.name]: params.value }));
        if ((params.name === "startOccurTime" || params.name === "endOccurTime") && params.value && params.value.format("YYYY-MM-DD") === "Invalid Date") {
            return;
        }
        setReverseSelect(is);
        setPage(0);
    };

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const initData = async () => {
        setIsLoading(true);
        let pageParam = {
            pageNo: page + 1,
            pageSize: rowsPerPage,
            reverseSelect: reverseSelect,
            sortOrderAsc: sortOrder[sortByField] === "asc",
            sortByField: sortByField,
            ...searchData
        };

        let params: any = { ...searchData };

        if (searchData.startOccurTime) {
            params.startOccurTime = searchData.startOccurTime.format("YYYY-MM-DD");
        }

        if (searchData.endOccurTime) {
            params.endOccurTime = searchData.endOccurTime.format("YYYY-MM-DD");
        }

        getTrackEvent(params, pageParam).then((res: any) => {
            if (res.code !== 200 || !res.data?.rows) {
                return;
            }
            setTableData(res.data.rows);
            setTotalCount(res.data.totalCount * 1 || 0);
            setIsLoading(false);
        });
    };

    const statusText = (status: string) => {
        switch (status) {
            case "00":
                return "active"
            case "01":
                return "ordinaryFailure"
            case "02":
                return "otherFailures"
            default:
                return "otherFailures"
        }
    }

    useEffect(() => {
        initData();
    }, [page, rowsPerPage, searchData, reverseSelect, sortByField, sortOrder]);


    return (
        <div className={styles.container}>
            <Toolbar style={{ justifyContent: "space-between", borderBottom: "1px solid #ccc", marginBottom: "5px", padding: "0" }}>
                <div>
                    <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        style={{ padding: "4px 10px", marginRight: "10px" }}
                        onClick={hanldeDownload}
                    >
                        {t("buttons.download")}
                    </Button>
                </div>

                <IconButton
                    color="success"
                    onClick={toggleFilters}
                    style={{
                        color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
                        background: showFilters ? "#c1e5ca" : "",
                    }}
                >
                    <span className="material-icons">filter_list</span>
                </IconButton>
            </Toolbar>

            {showFilters && (
                <Box style={{ display: "flex", alignItems: "center", padding: "8px", backgroundColor: "#f5f5f5", justifyContent: "space-between" }}>
                    <Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
                        <SearchInput onSearch={handleSearch} />

                        <StartTimeDropdown
                            defaultTime={searchData.startOccurTime}
                            onSave={(value) => handleSearch({ name: "startOccurTime", value }, reverseSelect)}
                        />
                        <EndTimeDropdown
                            defaultTime={searchData.endOccurTime}
                            onSave={(value) => handleSearch({ name: "endOccurTime", value }, reverseSelect)}
                        />
                    </Box>
                    <IconButton
                        onClick={() => setShowFilters(false)}
                        aria-label="clear"
                        color="default"
                    >
                        <span className="material-icons">clear</span>
                    </IconButton>
                </Box>
            )}

            <div style={{ flex: 1, overflow: "auto" }}>
                <Table
                    stickyHeader
                    aria-label="sticky  table"
                    size="small"
                    style={{
                        tableLayout: "fixed",
                        width: "100%",
                        minWidth: "100%"
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell style={{
                                width: 130,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "client_name"}
                                    onClick={() => {
                                        setSortByField("client_name")
                                        let sort: any = {
                                            ...sortOrder,
                                            client_name: sortOrder.client_name === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.client_name}
                                >

                                    {t("userSystem.clientName")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 130,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "bill_name"}
                                    onClick={() => {
                                        setSortByField("bill_name")
                                        let sort: any = {
                                            ...sortOrder,
                                            bill_name: sortOrder.bill_name === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.bill_name}
                                >

                                    {t("userSystem.billName")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 130,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "key_code"}
                                    onClick={() => {
                                        setSortByField("key_code")
                                        let sort: any = {
                                            ...sortOrder,
                                            key_code: sortOrder.key_code === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.key_code}
                                >

                                    {t("userSystem.keyCode")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 150,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "endpoint_name"}
                                    onClick={() => {
                                        setSortByField("endpoint_name")
                                        let sort: any = {
                                            ...sortOrder,
                                            endpoint_name: sortOrder.endpoint_name === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.endpoint_name}
                                >

                                    {t("userSystem.endPointName")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 130,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "http_method"}
                                    onClick={() => {
                                        setSortByField("http_method")
                                        let sort: any = {
                                            ...sortOrder,
                                            http_method: sortOrder.http_method === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.http_method}
                                >

                                    {t("userSystem.httpMethod")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 500,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "req_params"}
                                    onClick={() => {
                                        setSortByField("req_params")
                                        let sort: any = {
                                            ...sortOrder,
                                            req_params: sortOrder.req_params === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.req_params}
                                >

                                    {t("userSystem.reqParams")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 500,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "req_body"}
                                    onClick={() => {
                                        setSortByField("req_body")
                                        let sort: any = {
                                            ...sortOrder,
                                            req_body: sortOrder.req_body === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.req_body}
                                >

                                    {t("userSystem.reqBody")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 130,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "location_length"}
                                    onClick={() => {
                                        setSortByField("location_length")
                                        let sort: any = {
                                            ...sortOrder,
                                            location_length: sortOrder.location_length === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.location_length}
                                >

                                    {t("userSystem.amount")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 130,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "location"}
                                    onClick={() => {
                                        setSortByField("location")
                                        let sort: any = {
                                            ...sortOrder,
                                            location: sortOrder.location === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.location}
                                >

                                    {t("userSystem.coordinates")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 300,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "layers"}
                                    onClick={() => {
                                        setSortByField("layers")
                                        let sort: any = {
                                            ...sortOrder,
                                            layers: sortOrder.layers === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.layers}
                                >

                                    {t("userSystem.layerNames")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 180,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "occur_time"}
                                    onClick={() => {
                                        setSortByField("occur_time")
                                        let sort: any = {
                                            ...sortOrder,
                                            occur_time: sortOrder.occur_time === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.occur_time}
                                >
                                    {t("userSystem.dateTime")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 100,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "status"}
                                    onClick={() => {
                                        setSortByField("status")
                                        let sort: any = {
                                            ...sortOrder,
                                            status: sortOrder.status === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.status}
                                >
                                    {t("userSystem.Status")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                width: 150,
                                whiteSpace: "nowrap",
                                padding: "6px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "error_info"}
                                    onClick={() => {
                                        setSortByField("error_info")
                                        let sort: any = {
                                            ...sortOrder,
                                            error_info: sortOrder.error_info === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.error_info}
                                >
                                    {t("userSystem.errorInfo")}
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={11}>
                                    <div className={styles.loading}>
                                        <div className="loading loading-spinner loading-lg"></div>
                                        <div>{t("layers.loading")}</div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            tableData.map((item: any) => (
                                <TableRow
                                    key={item.id}
                                    className={styles.row}
                                >
                                    <TableCell style={{ padding: "6px" }}>{item.clientName}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{item.billName}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{item.keyCode}</TableCell>
                                    <TableCell style={{ padding: "6px", wordBreak: "break-all" }}>{item.endpointName}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{item.httpMethod}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>
                                        <Tooltip
                                            title={item.reqParams}
                                            placement="top"
                                            slotProps={{
                                                popper: {
                                                    sx: {
                                                        "& .MuiTooltip-tooltip": {
                                                            wordBreak: "break-all",
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <div
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.reqParams}
                                            </div>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ padding: "6px" }}>
                                        <Tooltip
                                            title={item.reqBody}
                                            placement="top"
                                            slotProps={{
                                                popper: {
                                                    sx: {
                                                        "& .MuiTooltip-tooltip": {
                                                            wordBreak: "break-all",
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <div
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.reqBody}
                                            </div>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ padding: "6px", wordBreak: "break-all" }}>{item.locationLength}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>
                                        <Tooltip
                                            title={item.location}
                                            placement="top"
                                            slotProps={{
                                                popper: {
                                                    sx: {
                                                        "& .MuiTooltip-tooltip": {
                                                            wordBreak: "break-all",
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <div
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.location}
                                            </div>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ padding: "6px" }}>
                                        <Tooltip
                                            title={item.layers}
                                            placement="top"
                                            slotProps={{
                                                popper: {
                                                    sx: {
                                                        "& .MuiTooltip-tooltip": {
                                                            wordBreak: "break-all",
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <div
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.layers}
                                            </div>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ padding: "6px" }} >{item.occurTime}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{t("userSystem." + statusText(item.status))}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>
                                        <Tooltip
                                            title={item.errorInfo}
                                            placement="top"
                                            slotProps={{
                                                popper: {
                                                    sx: {
                                                        "& .MuiTooltip-tooltip": {
                                                            wordBreak: "break-all",
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <div
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.errorInfo}
                                            </div>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
            <div style={{ marginLeft: "auto" }}>
                <TablePagination
                    component="div"
                    count={totalCount}
                    rowsPerPageOptions={[3, 10, 15, 25]}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("userSystem.rowsPerPage")}
                    slotProps={{
                        select: {
                            native: true,
                        },
                    }}
                />
            </div>
        </div >
    );
}

export default BurialPointDetails;
